import { motion } from 'framer-motion';
import { ArrowLeft, X, MessageCircle, Crown, Sparkles, Zap } from 'lucide-react';
import { brand } from '@iheart/shared';
import { useNavigate } from 'react-router-dom';
import { useChatWidgetStore } from './ChatWidgetStore';
import { useChatRooms } from '../../lib/chat';
import { useCurrentWorkspace } from '../../lib/workspace';
import { useDominaStatus } from '../../lib/domina';
import { WidgetLobby } from './WidgetLobby';
import { WidgetThread } from './WidgetThread';
import { WidgetDomina } from './WidgetDomina';
import { WidgetNewChat } from './WidgetNewChat';
import { useEffect } from 'react';

const VIEW_TITLES: Record<string, string> = {
  lobby: 'Workspace Chats',
  thread: 'Conversation',
  domina: 'Domina AI',
  'new-chat': 'New Chat',
};

export function ChatWidgetPanel() {
  const { view, activeRoomId, close, goToLobby, dominaMode } = useChatWidgetStore();
  const rooms = useChatRooms();
  const ws = useCurrentWorkspace();
  // Only the workspace-chats sub-views go "back" (to the chats list). Domina and
  // the chats list are top-level destinations (their own bubbles) — no back arrow,
  // so tapping Domina keeps you in Domina.
  const showBack = view === 'thread' || view === 'new-chat';
  const wsInitials = ws?.name
    ? ws.name
        .split(' ')
        .map((w) => w[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '';

  // Find active room name for the header
  const activeRoom = rooms.data?.find((r) => r.publicId === activeRoomId);
  const title =
    view === 'thread' && activeRoom
      ? activeRoom.name ?? activeRoom.lastSenderName ?? 'Chat'
      : view === 'domina'
        ? dominaMode === 'ai'
          ? 'Domina AI'
          : 'Domina Agent'
        : VIEW_TITLES[view] ?? 'Chat';

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [close]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={brand.motion.spring}
      // Keep the chat independent of the rail dropdowns (notifications / quick-add):
      // their click-outside listens on document mousedown, so without this, clicking
      // anywhere in the chat — including its X — would also close whichever dropdown
      // is open. Stopping the mousedown here keeps the two from closing together.
      onMouseDown={(e) => e.stopPropagation()}
      className={
        // Mobile: full-screen sheet (with a small safe-area inset) so it fits
        // any phone. Tablet/desktop (sm+): a floating panel anchored bottom-right,
        // capped to the viewport so it never overflows on short/small screens.
        'fixed z-50 flex flex-col overflow-hidden border border-white/30 bg-white shadow-2xl ' +
        'inset-2 rounded-2xl ' +
        'sm:inset-auto sm:bottom-20 sm:right-3 sm:h-[min(640px,calc(100vh-4rem))] sm:w-[min(380px,calc(100vw-1.5rem))] ' +
        'md:bottom-24 md:right-5'
      }
    >
      {/* Header */}
      <div
        className="flex shrink-0 items-center gap-2 px-3 py-3"
        style={{ backgroundImage: brand.gradient.primary }}
      >
        {showBack ? (
          <button
            type="button"
            onClick={goToLobby}
            className="flex size-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30"
          >
            <ArrowLeft className="size-4" />
          </button>
        ) : (
          <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/20 text-[11px] font-bold text-white">
            {ws?.logoUrl ? (
              <img src={ws.logoUrl} alt="" className="size-full object-cover" />
            ) : wsInitials ? (
              wsInitials
            ) : (
              <span className="relative inline-flex items-center justify-center">
                <Crown className="size-4" />
                <Sparkles className="absolute -right-1 -top-1 size-2" />
              </span>
            )}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold leading-tight text-white">{title}</span>
          {ws?.name && (
            <span className="block truncate text-[11px] leading-tight text-white/80">{ws.name}</span>
          )}
        </div>
        {view === 'domina' && dominaMode === 'ai' && <DominaHeaderCredits />}
        <button
          type="button"
          onClick={close}
          className="flex size-7 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30"
        >
          <X className="size-4" />
        </button>
      </div>

      {/* Active view */}
      {view === 'lobby' && <WidgetLobby />}
      {view === 'thread' && activeRoomId && <WidgetThread roomPublicId={activeRoomId} />}
      {view === 'domina' && <WidgetDomina />}
      {view === 'new-chat' && <WidgetNewChat />}
    </motion.div>
  );
}

/**
 * AI-credit indicator in the Domina header. Internal workspaces show nothing
 * (unlimited); everyone else sees their credit balance counting down with a
 * "Top up" shortcut, or a "Buy tokens now" call-to-action when out of credits.
 */
function DominaHeaderCredits() {
  const navigate = useNavigate();
  const { close } = useChatWidgetStore();
  const status = useDominaStatus();

  const goBilling = () => {
    close();
    navigate('/settings/billing');
  };

  if (!status.data || status.data.unlimited) return null;
  const { balance, blocked } = status.data;

  if (blocked) {
    return (
      <button
        type="button"
        onClick={goBilling}
        className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[11px] font-bold text-pink-600 shadow-sm transition-transform hover:scale-105"
        title="You're out of AI credits — buy more to keep using Domina"
      >
        Buy tokens now
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={goBilling}
      className="flex shrink-0 items-center gap-1 rounded-full bg-white/20 px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-white/30"
      title="AI credits remaining — tap to top up"
    >
      <Zap className="size-3" />
      {balance.toLocaleString('en-ZA')}
      <span className="ml-0.5 rounded-full bg-white/25 px-1.5 py-0.5 text-[10px] font-bold">Top up</span>
    </button>
  );
}
